import type { ButtonProps, ButtonSize, ButtonVariant } from './Button.types'

/**
 * button — feature-blind button primitive.
 *
 * Controlled wrapper over the native <button>: holds no state, fetches no
 * data. All appearance flows from the shared theme tokens via
 * `buttonClassName`, the single source of the button visual (also reusable
 * by sibling primitives that need the same look without a <button>).
 */

/**
 * The variants. `primary`, `urgent`, `critical` and `info` are the theme's
 * ACTION LADDER (theme-tokens design rule 4) — the fill states how much
 * attention the item needs: primary = act, no time pressure; urgent = act soon,
 * the deadline is close; critical = the deadline has PASSED; info = nothing to
 * do, just something to look at. All four carry the white action label.
 *
 * `primaryOutline` is the primary rung WITHOUT the fill: the same action
 * offered a second time on a screen that already carries it filled, because two
 * solid navy buttons for one action read as two different actions. ⛔ It is not
 * a rung of its own — nothing may use it for an action the screen does not also
 * offer as `primary`.
 *
 * `danger` is NOT part of that ladder: it marks a DESTRUCTIVE operation and so
 * fills with the status trio's text-safe tone (PLOMME), not the KORALL signal
 * colour. `secondary` and `ghost` are unfilled and carry no attention meaning.
 */
const variants: Record<ButtonVariant, string> = {
  primary: 'bg-action-primary text-action-contrast hover:opacity-90',
  primaryOutline:
    'bg-surface text-action-primary border border-action-primary hover:bg-surface-muted',
  urgent: 'bg-action-urgent text-action-contrast hover:opacity-90',
  critical: 'bg-action-critical text-action-contrast hover:opacity-90',
  info: 'bg-action-info text-action-contrast hover:opacity-90',
  secondary: 'bg-surface text-text border border-border-control hover:bg-surface-muted',
  ghost: 'bg-transparent text-text hover:bg-surface-muted',
  danger: 'bg-action-destructive text-action-contrast hover:opacity-90',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
}

/** Pure, deterministic visual helper — the only source of the button appearance. */
export function buttonClassName(variant: ButtonVariant, size: ButtonSize): string {
  return `inline-flex cursor-pointer items-center justify-center gap-2 rounded-md font-medium transition-opacity outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-default disabled:opacity-50 ${variants[variant]} ${sizes[size]}`
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClassName(variant, size)}
    >
      {loading ? (
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : null}
      {children}
    </button>
  )
}
